import { DataTable, DataTableSortStatus } from 'mantine-datatable';
import { useEffect, useLayoutEffect, useState } from 'react';
import { Link, useOutletContext, useSearchParams } from 'react-router-dom';

import {
  ActionIcon,
  Button,
  Group,
  Switch,
  TextInput,
  Tooltip,
} from '@mantine/core';
import { openConfirmModal, openModal } from '@mantine/modals';
import {
  IconInfoSquareRounded,
  IconPlus,
  IconSearch,
  IconTrash,
  IconX,
} from '@tabler/icons-react';

import {
  GetUsersRequestParams,
  GetUsersSortBy,
  User,
} from '@interfaces/users.interface';

import {
  useActivateUserMutation,
  useCountUsersQuery,
  useDeactivateUserMutation,
  useDeleteUserMutation,
  useGetUsersQuery,
} from '@api/users.api';

import useAuth from '@hooks/useAuth';
import useStateWithDebounce from '@hooks/useStateWithDebouce';

import CreatePasswordlessAdminForm from '@components/CreatePasswordlessAdminForm';
import { LayoutContextType } from '@components/layout/Layout';

export default function AdminList() {
  // ==========================================================================
  // General
  // ==========================================================================
  const { user } = useAuth();

  // ==========================================================================
  // State
  // ==========================================================================
  const [searchParams] = useSearchParams();

  const [filters, setFilters] = useState<GetUsersRequestParams>({
    page: +(searchParams.get('page') || 1),
    pageLength: +(searchParams.get('pageLength') || 50),
    role: 'admin',
    sortBy: 'id',
    sortOrder: 'desc',
  });

  const [firstNameSearch, setFirstNameSearch, firstNameSearchDebounced] =
    useStateWithDebounce('');
  const [lastNameSearch, setLastNameSearch, lastNameSearchDebounced] =
    useStateWithDebounce('');
  const [emailSearch, setEmailSearch, emailSearchDebounced] =
    useStateWithDebounce('');

  const [sortStatus, setSortStatus] = useState<DataTableSortStatus<User>>({
    columnAccessor: 'id',
    direction: 'desc',
  });

  useEffect(() => {
    // Skip the initial mount
    if (
      filters.sortBy === sortStatus.columnAccessor &&
      filters.sortOrder === sortStatus.direction
    ) {
      return;
    }

    setFilters((filters) => ({
      ...filters,
      sortBy: sortStatus.columnAccessor as GetUsersSortBy,
      sortOrder: sortStatus.direction,
    }));
  }, [sortStatus]);

  // ==========================================================================
  // Api
  // ==========================================================================
  const { data: userCount = { count: 0 }, isLoading: isLoadingCount } =
    useCountUsersQuery({ firstNameSearch, lastNameSearch, emailSearch });

  const { data = [], isLoading } = useGetUsersQuery({
    ...filters,
    firstNameSearch: firstNameSearchDebounced,
    lastNameSearch: lastNameSearchDebounced,
    emailSearch: emailSearchDebounced,
  });

  const [deleteUser] = useDeleteUserMutation();

  const [activateUser, { isLoading: isLoadingActivateUser }] =
    useActivateUserMutation();
  const [deactivateUser, { isLoading: isLoadingDeactivateUser }] =
    useDeactivateUserMutation();

  // ==========================================================================
  // Handlers
  // ==========================================================================
  const handleDeleteUser = (user: User) => {
    openConfirmModal({
      title: 'Elimina utente',
      children: `Stai per eliminare l'utente ${user.firstName} ${user.lastName}. Confermare?`,
      labels: {
        cancel: 'Annulla',
        confirm: 'Elimina utente',
      },
      confirmProps: { color: 'red' },
      onConfirm: () => deleteUser(user.id),
    });
  };

  const handleCreateUser = () => {
    openModal({
      title: 'Crea utente amministratore',
      children: <CreatePasswordlessAdminForm />,
    });
  };

  // ==========================================================================
  // Render
  // ==========================================================================
  const { setLayoutProps } = useOutletContext<LayoutContextType>();

  useLayoutEffect(() => {
    setLayoutProps({
      title: 'Utenti amministratori',
      rightSection: (
        <Button onClick={handleCreateUser} leftSection={<IconPlus />}>
          Crea amministratore
        </Button>
      ),
    });
  }, [setLayoutProps]);

  return (
    <DataTable
      fetching={isLoading || isLoadingCount}
      columns={[
        {
          accessor: 'active',
          title: 'Attivo',
          width: '0%',
          render: (record) => (
            <Group wrap="nowrap" gap="xs">
              <Tooltip
                label={
                  record.id === user?.id
                    ? 'Non puoi disabilitare il tuo account'
                    : record.activationTokenCreatedAt
                      ? "L'utente non ha ancora attivato il suo account"
                      : record.active
                        ? 'Disabilita'
                        : 'Abilita'
                }
              >
                <div>
                  <Switch
                    disabled={
                      record.id === user?.id ||
                      record.activationTokenCreatedAt !== null ||
                      isLoadingActivateUser ||
                      isLoadingDeactivateUser
                    }
                    checked={record.active}
                    onClick={
                      record.active
                        ? () => deactivateUser(record.id)
                        : () => activateUser(record.id)
                    }
                  />
                </div>
              </Tooltip>
            </Group>
          ),
        },
        {
          accessor: 'firstName',
          title: 'Nome',
          sortable: true,
          filter: (
            <TextInput
              placeholder="Ricerca nome"
              leftSection={<IconSearch size={16} />}
              rightSection={
                firstNameSearch.trim() !== '' ? (
                  <ActionIcon
                    size="sm"
                    variant="transparent"
                    c="dimmed"
                    onClick={() => setFirstNameSearch('')}
                  >
                    <IconX size={14} />
                  </ActionIcon>
                ) : undefined
              }
              value={firstNameSearch}
              onChange={(e) => setFirstNameSearch(e.currentTarget.value)}
            />
          ),
        },
        {
          accessor: 'lastName',
          title: 'Cognome',
          sortable: true,
          filter: (
            <TextInput
              placeholder="Ricerca cognome"
              leftSection={<IconSearch size={16} />}
              rightSection={
                lastNameSearch.trim() !== '' ? (
                  <ActionIcon
                    size="sm"
                    variant="transparent"
                    c="dimmed"
                    onClick={() => setLastNameSearch('')}
                  >
                    <IconX size={14} />
                  </ActionIcon>
                ) : undefined
              }
              value={lastNameSearch}
              onChange={(e) => setLastNameSearch(e.currentTarget.value)}
            />
          ),
        },
        {
          accessor: 'email',
          sortable: true,
          filter: (
            <TextInput
              placeholder="Ricerca email"
              leftSection={<IconSearch size={16} />}
              rightSection={
                emailSearch.trim() !== '' ? (
                  <ActionIcon
                    size="sm"
                    variant="transparent"
                    c="dimmed"
                    onClick={() => setEmailSearch('')}
                  >
                    <IconX size={14} />
                  </ActionIcon>
                ) : undefined
              }
              value={emailSearch}
              onChange={(e) => setEmailSearch(e.currentTarget.value)}
            />
          ),
        },
        {
          accessor: 'actions',
          title: '',
          width: '0%',
          textAlign: 'right',
          render: (record) => (
            <Group wrap="nowrap" gap="xs">
              <Tooltip label="Dettagli">
                <ActionIcon
                  variant="subtle"
                  component={Link}
                  to={`/utenti/${record.id}`}
                >
                  <IconInfoSquareRounded />
                </ActionIcon>
              </Tooltip>
              <Tooltip label="Elimina">
                <ActionIcon
                  variant="subtle"
                  color="red"
                  disabled={record.id === user!.id}
                  onClick={() => handleDeleteUser(record)}
                >
                  <IconTrash />
                </ActionIcon>
              </Tooltip>
            </Group>
          ),
        },
      ]}
      records={data}
      page={filters.page!}
      onPageChange={(page) => setFilters((filters) => ({ ...filters, page }))}
      recordsPerPage={filters.pageLength!}
      totalRecords={userCount.count}
      sortStatus={sortStatus}
      onSortStatusChange={setSortStatus}
      noRecordsText="Nessun dato"
      minHeight={data.length === 0 ? 160 : undefined}
      striped
      highlightOnHover
      withTableBorder
    />
  );
}
